import { useCallback, useEffect, useMemo, useState } from 'react';
import { suppliersApi } from '@/api/suppliers.api';
import type { SelectOption } from '@/components/ui/Select';
import type { SupplierResponse } from '@/types/supplier';
import { formatSupplierLabel } from '@/utils/entityLabels';
import { useDebounce } from '@/hooks/useDebounce';
import { showApiError } from '@/utils/apiError';

function supplierOption(s: SupplierResponse): SelectOption {
  return { value: String(s.id), label: formatSupplierLabel(s) };
}

function mergeSelectedOption(options: SelectOption[], selected?: SelectOption | null): SelectOption[] {
  if (!selected) return options;
  if (options.some((opt) => opt.value === selected.value)) return options;
  return [selected, ...options];
}

type Params = { value?: string; pageSize?: number; activeOnly?: boolean };

export function useSupplierSelectSearch({ value = '', pageSize = 30, activeOnly = true }: Params = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [selected, setSelected] = useState<SupplierResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const load = useCallback(
    async (search: string) => {
      setSearching(true);
      try {
        const res = await suppliersApi.list({
          search: search || undefined,
          page: 0,
          size: pageSize,
          activeOnly,
        });
        setSuppliers(res.content);
      } catch (err) {
        showApiError(err, 'Failed to load suppliers');
      } finally {
        setSearching(false);
        setLoading(false);
      }
    },
    [activeOnly, pageSize],
  );

  useEffect(() => {
    void load(debouncedSearch);
  }, [debouncedSearch, load]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const id = Number(value);
    if (Number.isNaN(id)) return;
    const fromList = suppliers.find((s) => s.id === id);
    if (fromList) {
      setSelected(fromList);
      return;
    }
    let cancelled = false;
    suppliersApi
      .get(id)
      .then((row) => {
        if (!cancelled) setSelected(row);
      })
      .catch(() => {
        if (!cancelled) setSelected(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value, suppliers]);

  const options = useMemo(() => {
    const base = suppliers.map(supplierOption);
    const sel = selected ? supplierOption(selected) : null;
    return mergeSelectedOption(base, sel);
  }, [suppliers, selected]);

  const byId = useMemo(() => {
    const map = new Map<number, SupplierResponse>();
    for (const s of suppliers) map.set(s.id, s);
    if (selected) map.set(selected.id, selected);
    return map;
  }, [suppliers, selected]);

  const getSupplier = useCallback(
    (id: number | string | null | undefined) => {
      if (id == null || id === '') return undefined;
      return byId.get(Number(id));
    },
    [byId],
  );

  return { options, loading, searching, onSearchQuery: setSearchQuery, getSupplier };
}
